import React, { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Reveal from "../components/site/Reveal";
import { CtaLink, Amp, LogoTitle } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { FAQ_THEMES } from "../faqData";

const fold = (s) => [...s].map((c) => c.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()[0] || c).join("");

function Hl({ text, q }) {
  if (!q) return text;
  const f = fold(text), out = [];
  let last = 0, i = f.indexOf(q);
  while (i >= 0) {
    out.push(text.slice(last, i), <mark key={i} className="bg-[#d9895a]/30 text-inherit rounded px-0.5">{text.slice(i, i + q.length)}</mark>);
    last = i + q.length;
    i = f.indexOf(q, last);
  }
  out.push(text.slice(last));
  return out;
}

function FaqHero({ q, setQ, count }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]" data-testid="faq-hero">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#d9895a]/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-14 relative">
        <Reveal><span className="text-sm font-bold uppercase tracking-[0.2em] text-[#c4703f]">FAQ propriétaires</span></Reveal>
        <Reveal delay={80} className="mt-4"><LogoTitle title="vos questions" script="nos réponses" /></Reveal>
        <Reveal delay={160}><p className="mt-8 text-lg text-[#4a5249] max-w-xl leading-relaxed">Commission, paiement, clés, assurance, législation : tout ce qu'il faut savoir avant de nous confier votre logement.</p></Reveal>
        <Reveal delay={220} className="mt-8 max-w-xl">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6d7268]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Un mot : clés, assurance, paiement…" data-testid="faq-search"
              className="w-full h-14 rounded-full pl-12 pr-12 bg-white ring-1 ring-[#e6dcc8] focus:ring-2 focus:ring-[#d9895a] outline-none text-base text-[#13261c] placeholder:text-[#9a9d94] shadow-sm" />
            {q && <button onClick={() => setQ("")} data-testid="faq-search-clear" aria-label="Effacer" className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full hover:bg-[#f3ede1]"><X className="w-4 h-4" /></button>}
          </div>
          {q && <p className="mt-3 ml-5 text-sm font-semibold text-[#c4703f]" data-testid="faq-search-count">{count} réponse{count > 1 ? "s" : ""} trouvée{count > 1 ? "s" : ""}</p>}
        </Reveal>
      </div>
    </section>
  );
}

function ThemeBlock({ t, n, q }) {
  return (
    <div id={t.id} className="scroll-mt-28" data-testid={`faq-theme-${t.id}`}>
      <div className="flex items-end gap-3 mb-6">
        <span className="font-script text-[#d9895a] text-4xl leading-none">{String(n).padStart(2, "0")}</span>
        <h2 className="font-logo font-black text-3xl lg:text-4xl tracking-[-0.025em] text-[#13261c] leading-none"><Amp text={t.title} /><OrganicDot className="inline-block w-2.5 h-2.5 ml-1 align-baseline" /></h2>
      </div>
      <Accordion key={q ? "s" : "n"} type="multiple" defaultValue={q ? t.items.map((f) => f.q) : []} className="space-y-3">
        {t.items.map((f, i) => (
          <AccordionItem key={f.q} value={f.q} className="border border-[#e6dcc8] border-l-4 border-l-transparent rounded-2xl px-6 bg-[#f6f1e7] data-[state=open]:bg-white data-[state=open]:border-l-[#d9895a] data-[state=open]:shadow-[0_20px_40px_-25px_rgba(19,38,28,0.35)] transition-[background-color,box-shadow,border-color]">
            <AccordionTrigger data-testid={`faq-q-${t.id}-${f.idx}`} className="text-left text-[17px] font-bold text-[#13261c] hover:no-underline hover:text-[#c4703f] py-5"><span><Hl text={f.q} q={q} /></span></AccordionTrigger>
            <AccordionContent className="text-[15px] text-[#5a6158] leading-relaxed pb-5"><Hl text={f.a} q={q} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function NoResult({ onReset }) {
  return (
    <div className="rounded-[28px] bg-[#f3ede1] p-10 text-center" data-testid="faq-empty">
      <p className="font-logo font-black text-2xl text-[#13261c]">aucune réponse<OrganicDot className="inline-block w-2 h-2 ml-0.5 align-baseline" /></p>
      <p className="font-script text-[#d9895a] text-2xl">posez-nous la question directement</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button onClick={onReset} data-testid="faq-empty-reset" className="rounded-full bg-white ring-1 ring-[#dccfb6] px-6 py-3 font-bold text-[#13261c] hover:bg-[#faf7f0]">Voir toutes les questions</button>
        <CtaLink to="/contact">Nous contacter</CtaLink>
      </div>
    </div>
  );
}

export default function Faq() {
  const [raw, setQ] = useState("");
  const q = fold(raw.trim());
  const themes = useMemo(() => FAQ_THEMES.map((t, n) => ({ ...t, n: n + 1, items: t.items.map((f, idx) => ({ ...f, idx })).filter((f) => !q || fold(f.q + " " + f.a).includes(q)) })).filter((t) => t.items.length), [q]);
  const count = themes.reduce((a, t) => a + t.items.length, 0);
  return (
    <div data-testid="faq-page">
      <FaqHero q={raw} setQ={setQ} count={count} />
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <nav className="lg:col-span-3 lg:sticky lg:top-28 self-start flex lg:flex-col gap-2 overflow-x-auto pb-2" data-testid="faq-nav">
            {themes.map((t) => (
              <a key={t.id} href={`#${t.id}`} data-testid={`faq-nav-${t.id}`}
                className="group shrink-0 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-[#13261c] bg-[#f3ede1] hover:bg-[#13261c] hover:text-white transition-colors">
                <OrganicDot className="w-2 h-2 transition-transform group-hover:scale-150" />{t.title}
              </a>
            ))}
          </nav>
          <div className="lg:col-span-9 space-y-16">
            {count === 0 && <NoResult onReset={() => setQ("")} />}
            {themes.map((t) => <ThemeBlock key={t.id} t={t} n={t.n} q={q} />)}
            <div className="relative overflow-hidden rounded-[28px] bg-[#13261c] text-white p-8 lg:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="faq-cta">
              <div>
                <p className="font-logo font-black text-2xl lg:text-3xl tracking-[-0.025em]">pas trouvé votre réponse ?</p>
                <p className="font-script text-[#d9895a] text-2xl mt-1">on en parle de vive voix</p>
              </div>
              <CtaLink to="/contact">Nous contacter</CtaLink>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
